<template>
  <base-layout :full-width="true">
    <div class="bg-white relative isolate overflow-hidden -mt-14 sm:-mt-16">
      <!-- Decorative background patterns -->
      <div
        class="absolute inset-x-0 top-0 -z-10 transform-gpu overflow-hidden blur-3xl"
        aria-hidden="true"
      >
        <div
          class="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#4096ff] to-[#1677ff] opacity-[0.15] sm:left-[calc(25%-20rem)] sm:w-[72.1875rem]"
          style="
            clip-path: polygon(
              74.1% 44.1%,
              100% 61.6%,
              97.5% 26.9%,
              85.5% 0.1%,
              80.7% 2%,
              72.5% 32.5%,
              60.2% 62.4%,
              52.4% 68.1%,
              47.5% 58.3%,
              45.2% 34.5%,
              27.5% 76.7%,
              0.1% 64.9%,
              17.9% 100%,
              27.6% 76.8%,
              76.1% 97.7%,
              74.1% 44.1%
            );
          "
        />
      </div>
      <div
        class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        aria-hidden="true"
      >
        <div
          class="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#69b1ff] to-[#1677ff] opacity-[0.08] sm:left-[calc(25%+16rem)] sm:w-[72.1875rem]"
          style="
            clip-path: polygon(
              74.1% 44.1%,
              100% 61.6%,
              97.5% 26.9%,
              85.5% 0.1%,
              80.7% 2%,
              72.5% 32.5%,
              60.2% 62.4%,
              52.4% 68.1%,
              47.5% 58.3%,
              45.2% 34.5%,
              27.5% 76.7%,
              0.1% 64.9%,
              17.9% 100%,
              27.6% 76.8%,
              76.1% 97.7%,
              74.1% 44.1%
            );
          "
        />
      </div>
      <div class="mx-auto max-w-[1440px] px-6 lg:px-8 py-24 sm:py-32">
        <!-- 在大屏幕时使用两列布局 -->
        <div class="grid xl:grid-cols-[1fr,2fr] gap-20">
          <!-- 左侧标题区域 -->
          <div class="max-w-xl space-y-8">
            <div class="space-y-4">
              <h2
                class="text-4xl font-semibold tracking-tight text-pretty text-grey-85 sm:text-5xl"
              >
                {{ $t("about.title") }}
              </h2>
              <p class="text-lg font-medium text-primary-6">
                {{ $t("about.subtitle") }}
              </p>
            </div>
            <div class="space-y-4">
              <!-- <p class="text-base/7 text-grey-65 sm:text-lg/8">
                {{ $t("about.description") }}
              </p> -->
              <!-- 关键词标签云 -->
              <div
                class="tag-cloud-container h-28 sm:h-auto sm:!min-h-0 relative overflow-hidden w-full"
              >
                <div class="tag-scroll-wrapper">
                  <div
                    class="tag-scroll-row"
                    style="--scroll-speed: 80s; --row-offset: 0s"
                  >
                    <div class="tag-scroll-content">
                      <span
                        v-for="tag in shuffledTags.slice(0, 7)"
                        :key="tag.name"
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-1 text-primary-7 mx-1"
                      >
                        {{ tag.name }}
                      </span>
                      <span
                        v-for="tag in shuffledTags.slice(0, 7)"
                        :key="tag.name + '-dup'"
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-1 text-primary-7 mx-1"
                      >
                        {{ tag.name }}
                      </span>
                    </div>
                  </div>
                  <div
                    class="tag-scroll-row"
                    style="--scroll-speed: 120s; --row-offset: -20s"
                  >
                    <div class="tag-scroll-content">
                      <span
                        v-for="tag in shuffledTags.slice(7, 14)"
                        :key="tag.name"
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-1 text-primary-7 mx-1"
                      >
                        {{ tag.name }}
                      </span>
                      <span
                        v-for="tag in shuffledTags.slice(7, 14)"
                        :key="tag.name + '-dup'"
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-1 text-primary-7 mx-1"
                      >
                        {{ tag.name }}
                      </span>
                    </div>
                  </div>
                  <div
                    class="tag-scroll-row"
                    style="--scroll-speed: 100s; --row-offset: -40s"
                  >
                    <div class="tag-scroll-content">
                      <span
                        v-for="tag in shuffledTags.slice(14)"
                        :key="tag.name"
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-1 text-primary-7 mx-1"
                      >
                        {{ tag.name }}
                      </span>
                      <span
                        v-for="tag in shuffledTags.slice(14)"
                        :key="tag.name + '-dup'"
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-1 text-primary-7 mx-1"
                      >
                        {{ tag.name }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧成员列表区域 -->
          <div class="w-full relative">
            <!-- 装饰性背景图案 -->
            <div
              class="absolute inset-0 -z-10 opacity-[0.02] pointer-events-none"
            >
              <background-patterns />
            </div>

            <!-- 指导老师单独一行，居中显示 -->
            <div v-if="supervisor" class="flex justify-center mb-16">
              <div class="flex items-start gap-x-6">
                <img
                  v-if="supervisor.email"
                  class="size-16 rounded-full ring-2 ring-primary-2 ring-offset-2"
                  :src="supervisor.imageUrl"
                  :alt="supervisor.name"
                />
                <div
                  v-else
                  class="size-16 rounded-full flex items-center justify-center text-white font-semibold text-lg ring-2 ring-primary-2 ring-offset-2 default-avatar"
                  :style="{ backgroundColor: supervisor.avatarColor }"
                >
                  {{ supervisor.initials }}
                </div>
                <div class="flex flex-col pt-2">
                  <h3
                    class="text-base/7 font-semibold tracking-tight text-grey-85"
                  >
                    {{ supervisor.name }}
                  </h3>
                  <p class="text-sm/6 font-medium text-primary-6">
                    {{ supervisor.role }}
                  </p>
                  <p
                    v-if="supervisor.slogan"
                    class="mt-2 text-sm/6 text-grey-65"
                  >
                    {{ supervisor.slogan }}
                  </p>
                </div>
              </div>
            </div>

            <!-- 其他成员网格布局 -->
            <div class="grid sm:grid-cols-2 gap-x-12 gap-y-16">
              <div
                v-for="person in teamMembers"
                :key="person.name"
                class="flex items-start gap-x-6 group"
              >
                <img
                  v-if="person.email"
                  class="size-16 rounded-full ring-2 ring-primary-2 ring-offset-2 transition-transform duration-300 group-hover:scale-110 mt-2"
                  :src="person.imageUrl"
                  :alt="person.name"
                />
                <div
                  v-else
                  class="size-16 rounded-full flex items-center justify-center text-white font-semibold text-lg ring-2 ring-primary-2 ring-offset-2 transition-transform duration-300 group-hover:scale-110 default-avatar mt-2"
                  :style="{ backgroundColor: person.avatarColor }"
                >
                  {{ person.initials }}
                </div>
                <div class="flex flex-col min-w-0 pt-2">
                  <h3
                    class="text-base/7 font-semibold tracking-tight text-grey-85 truncate"
                  >
                    {{ person.name }}
                  </h3>
                  <p class="text-sm/6 font-medium text-primary-6 truncate">
                    {{ person.role }}
                  </p>
                  <p class="mt-2 text-sm/6 text-grey-65 break-words">
                    {{ person.slogan }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </base-layout>
</template>

<script setup lang="ts">
import BaseLayout from "@/components/layout/BaseLayout.vue";
import { computed, ref } from "vue";
import md5 from "md5";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// 关键词标签
const tags = [
  // 核心技术栈
  { name: "Vue 3" },
  { name: "TypeScript" },
  { name: "Vite" },
  { name: "Pinia" },
  { name: "Vue Router" },
  { name: "Ant Design Vue" },
  { name: "Tailwind CSS" },

  // 开发工具和库
  { name: "Node.js" },
  { name: "Yarn" },
  { name: "Git" },
  { name: "Prettier" },
  { name: "ESLint" },

  // 功能和服务
  { name: "SSO" },
  { name: "JWT" },
  { name: "REST API" },
  { name: "i18n" },
  { name: "OAuth" },

  // 基础设施
  { name: "Docker" },
  { name: "Nginx" },
  { name: "CI/CD" },
  { name: "Jenkins" },
  { name: "DevOps" },

  // 监控和性能
  { name: "Sentry" },
  { name: "Performance" },
  { name: "Analytics" },

  // 安全
  { name: "HTTPS" },
  { name: "CORS" },
  { name: "Security" },

  // 子系统
  { name: "Signin" },
  { name: "CE System" },
  { name: "Library" },
  { name: "Office Hour" },
];

// 计算属性：随机排序的标签
const shuffledTags = computed(() => {
  return [...tags].sort(() => Math.random() - 0.5);
});

const people = [
  {
    name: "贺超",
    role: "指导老师",
    slogan: "",
    email: "",
  },
  {
    name: "别为馒头",
    role: "UI / 前端开发",
    slogan: "你以为我是 UI，其实我是前端；你以为我是前端，其实我是 UI",
    email: "ding@ygxz.in",
  },
  {
    name: "AptS:1547",
    role: "运维 / 后端开发",
    slogan: "我们在少量的 Nginx 配置文件中找到了大量的 CORS 跨域错误",
    email: "image@esaps.net",
  },
  {
    name: "uaih3k9x",
    role: "架构 / 后端开发",
    slogan: "It's My构！！！！！",
    email: "uaih3k9x@gmail.com",
  },
  {
    name: "Bt1Q",
    role: "运维 / 服务端开发",
    slogan: "一子莫，塔塔开!",
    email: "zhihong114514@gmail.com",
  },
  {
    name: "sqrt3",
    role: "后端开发",
    slogan: "爆搜挂着机，打表出省一",
    email: "1273750248@qq.com",
  },
  {
    name: "INFAMOUSGXY",
    role: "硬件运维 / 后端开发",
    slogan: "Love you Nvidia",
    email: "xinyiguo517@gmail.com",
  },
];

const avatarColors = [
  "#597ef7", // primary-5
  "#2f54eb", // primary-6
  "#1d39c4", // primary-7
  "#10239e", // primary-8
];

// 工具函数：获取姓名的首字母
const getInitials = (name: string) => {
  return name.charAt(0);
};

// 计算属性：获取指导老师信息
const supervisor = computed(() => {
  const supervisorData = people[0];
  return {
    ...supervisorData,
    imageUrl: supervisorData.email
      ? `https://cn.cravatar.com/avatar/${md5(supervisorData.email.trim().toLowerCase())}?d=retro&s=512`
      : "",
    initials: getInitials(supervisorData.name),
    avatarColor: avatarColors[0],
  };
});

// 计算属性：获取随机排序的团队成员信息
const teamMembers = computed(() => {
  // 获取除了指导老师以外的成员并随机排序
  const shuffledMembers = people.slice(1).sort(() => Math.random() - 0.5);

  return shuffledMembers.map((person, index) => ({
    ...person,
    imageUrl: person.email
      ? `https://cn.cravatar.com/avatar/${md5(person.email.trim().toLowerCase())}?d=retro&s=512`
      : "",
    initials: getInitials(person.name),
    avatarColor: avatarColors[(index + 1) % avatarColors.length],
  }));
});
</script>

<style scoped>
.tag-cloud-container {
  min-height: 7rem;
  max-width: 100vw;
  margin-left: -1.5rem;
  margin-right: -1.5rem;
  padding: 0 1.5rem;
  width: 100%;
}

.tag-scroll-wrapper {
  width: 100%;
  overflow: hidden;
}

.tag-scroll-row {
  position: relative;
  height: 2.25rem;
  margin: 0.25rem 0;
  overflow: hidden;
  white-space: nowrap;
  mask-image: linear-gradient(
    to right,
    transparent,
    black 10%,
    black 90%,
    transparent
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black 10%,
    black 90%,
    transparent
  );
}

.tag-scroll-content {
  display: inline-block;
  animation: scrollTags var(--scroll-speed) linear var(--row-offset) infinite;
  padding-right: 2rem;
}

@keyframes scrollTags {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@media (min-width: 640px) {
  .tag-cloud-container {
    margin-left: 0;
    margin-right: 0;
    padding: 0;
    min-height: 7rem;
    height: auto;
    width: 100%;
    max-width: 400px;
  }

  .tag-scroll-wrapper {
    width: 100%;
  }

  .tag-scroll-row {
    height: 2.25rem;
    margin: 0.25rem 0;
  }

  .tag-scroll-content {
    font-size: 0.875rem;
  }

  .tag-scroll-content span {
    padding: 0.25rem 0.75rem;
  }
}

.default-avatar {
  transition: all 0.3s ease;
}

.default-avatar:hover {
  transform: scale(1.05);
}
</style>
